import { Component, OnInit } from '@angular/core';
import { faTag } from '@fortawesome/pro-duotone-svg-icons';
import { Store } from '@ngxs/store';
import _ from 'lodash';
import { Observable, combineLatest } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, switchMap } from 'rxjs/operators';
import { ShortcutDescription } from 'src/app/samwebDescription/samweb/shortcuts/shortcutDescription';
import { SingleCallTagBehaviourSelectors } from 'src/app/store/dashboard/brick-wall/brick-behaviour-state/brickTypes/single-call-tag-behaviour/single-call-tag-behaviour.selectors';
import { SingleCallTagBehaviourState } from 'src/app/store/dashboard/brick-wall/brick-behaviour-state/brickTypes/single-call-tag-behaviour/single-call-tag-behaviour.state';
import { UpdateCallTag } from 'src/app/store/samwin/call-tags/call-tags.actions';
import { CallTagsSelectors } from 'src/app/store/samwin/call-tags/call-tags.selectors';
import { HOTKEYS_DISABLED } from 'src/app/store/samwin/shortcut-behaviour/hotkeys-disabled';
import { ConfigurableEditorSettingRemoteDto, SingleCallTagEditorBrickSettingRemoteDto } from '../../../../../generated/swagger';
import { BrickCategory } from '../../../../samwebDescription/samweb/brickCategoryType';
import { EditorType } from '../../../../samwebDescription/samweb/editorType';
import { PresentationType } from '../../../../samwebDescription/samweb/settings/presentationType';
import { SetSingleTagTypingState } from '../../../../store/dashboard/brick-wall/brick-behaviour-state/brickTypes/single-call-tag-behaviour/single-call-tag-behaviour.actions';
import { SingleCallTagBrickSettingsState } from '../../../../store/dashboard/brick-wall/brick-settings/specific-brick-settings/single-call-tag-brick-settings/single-call-tag-brick-settings.state';
import { BasicBrickComponent } from '../../basic-brick-component';
import { SamwebBrick } from '../../samweb-brick.decorator';
import { SingleCallTagEditorSettingsComponent } from './single-call-tag-editor-settings/single-call-tag-editor-settings.component';

@Component({
   selector: 'samweb-single-call-tag-editor-brick',
   templateUrl: './single-call-tag-editor-brick.component.html',
   styleUrls: ['./single-call-tag-editor-brick.component.scss'],
})
@SamwebBrick({
   brickDefinitionId: 'singleCallTagEditorBrick',
   brickTitleTranslationKey: 'brick.brick_definitions.single_call_tag_editor.title',
   brickDescriptionTranslationKey: 'brick.brick_definitions.single_call_tag_editor.description',
   brickIcon: faTag,
   minColWidth: 5,
   minRowHeight: 4,
   brickCategory: BrickCategory.GENERAL,
   settingsComponent: SingleCallTagEditorSettingsComponent,
   settingStateHandler: SingleCallTagBrickSettingsState,
})
export class SingleCallTagEditorBrickComponent extends BasicBrickComponent implements OnInit {
   public callTagValue$: Observable<string>;

   public brickInstanceId: Readonly<string>;

   public settings$: Observable<SingleCallTagEditorBrickSettingRemoteDto>;

   public tagDisplayName$: Observable<string>;

   public isCallTagReadOnly$: Observable<boolean>;

   public isTagEditable$: Observable<boolean>;

   public isTyping$: Observable<boolean>;

   public presentationType$: Observable<PresentationType>;

   public selectedEditorType$: Observable<EditorType>;

   public configurableEditorSettings$: Observable<ConfigurableEditorSettingRemoteDto>;

   public isClearButtonVisible$: Observable<boolean>;
   public clearTagValue$: Observable<string>;

   constructor(private store: Store) {
      super();
   }

   public keyActions: ShortcutDescription[] = [
      {
         shortcutKey: HOTKEYS_DISABLED,
         action: () => {
            return true;
         },
      },
   ];

   ngOnInit() {
      this.clearTagValue$ = this.store.select(SingleCallTagBrickSettingsState.getClearTagValue).pipe(map((getValue) => getValue(this.brickInstanceId)));
      const currentCalltag$ = this.store.select(SingleCallTagBehaviourSelectors.getCallTagValue).pipe(
         distinctUntilChanged((prev, curr) => _.isEqual(prev, curr)),
         map((fn) => fn(this.brickInstanceId))
      );

      this.isTyping$ = this.store.select(SingleCallTagBehaviourState.isTyping).pipe(
         debounceTime(300),
         distinctUntilChanged((prev, curr) => _.isEqual(prev, curr)),
         map((getIsTyping) => getIsTyping(this.brickInstanceId))
      );

      this.callTagValue$ = combineLatest([currentCalltag$, this.isTyping$]).pipe(
         filter(([currentCalltag, isTyping]) => !isTyping),
         map(([currentCalltag]) => currentCalltag)
      );

      this.isTagEditable$ = this.store
         .select(SingleCallTagBehaviourSelectors.getIsCallTagEditable)
         .pipe(map((getIsCallTagEditable) => getIsCallTagEditable(this.brickInstanceId)));

      this.settings$ = this.store.select(SingleCallTagBrickSettingsState.getSettings).pipe(map((getEditorSetting) => getEditorSetting(this.brickInstanceId)));
      this.configurableEditorSettings$ = this.settings$.pipe(map((x) => x.editorSettings));

      this.isCallTagReadOnly$ = this.store.select(SingleCallTagBehaviourSelectors.getIsCallTagEditable).pipe(
         map((fn) => fn(this.brickInstanceId)),
         map((isCallTagEditable) => !isCallTagEditable)
      );

      this.selectedEditorType$ = this.store.select(SingleCallTagBrickSettingsState.getEditorType).pipe(map((fn) => fn(this.brickInstanceId)));

      this.presentationType$ = this.store.select(SingleCallTagBrickSettingsState.getTagPresentationType).pipe(map((fn) => fn(this.brickInstanceId)));
      this.tagDisplayName$ = this.settings$.pipe(
         map((settings) => (_.isEmpty(settings.callTagDisplayName) ? settings.callTagId : settings.callTagDisplayName))
      );

      this.isClearButtonVisible$ = this.isCallTagReadOnly$.pipe(
         switchMap((isCallTagReadOnly: boolean) =>
            this.settings$.pipe(
               switchMap((settings) =>
                  this.selectedEditorType$.pipe(
                     map((selectedEditorType) => !isCallTagReadOnly && !settings.readOnlyMode && selectedEditorType.value !== EditorType.CHECK_BOX.value)
                  )
               )
            )
         )
      );
   }

   setIsTyping(isTyping: boolean) {
      this.store.dispatch(new SetSingleTagTypingState(this.brickInstanceId, isTyping));
   }

   onTagValueChanged(callTag: string) {
      this.updateCallTagValue(callTag);
   }

   updateCallTagValue(callTag: string) {
      const callTagKey = this.store.selectSnapshot(SingleCallTagBrickSettingsState.getCallTagId)(this.brickInstanceId);
      const callId = this.store.selectSnapshot(CallTagsSelectors.lastActiveCallIdWithTag);
      this.store.dispatch(new UpdateCallTag(callId, callTagKey, callTag));
   }
}
